import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { pathFor } from '../../../helpers/links';
import { useDraws } from '../../../providers/DrawProvider';
import { useProducts } from '../../../providers/ProductProvider';
import { CheckoutSelectionChange, CheckoutWorksController } from '../types';
import { resolveCheckoutIntentKind } from '../utils/intentKind';
import { DEFAULT_CHECKOUT_STEP } from '../utils/steps';
import { buildCheckoutWork } from '../utils/workView';
import { CheckoutStepController } from './useCheckoutStep';

type UseDrawWorksArgs = {
  step: CheckoutStepController;
};

export const useDrawWorks = ({
  step,
}: UseDrawWorksArgs): CheckoutWorksController => {
  const router = useRouter();
  const { getProduct } = useProducts();
  const {
    drawIdsEntered: enteredDrawIds,
    isAccountInDraw: isEntered,
    linkedDraws,
    mainDraw,
    selectDraw,
    selectedDraws,
    setSelectedDraws,
    setWinningDrawsCount: setMaxQuantity,
    winningDrawsCount: maxQuantity,
  } = useDraws();
  const isSingleDraw = resolveCheckoutIntentKind(mainDraw?.draw) === 'single';

  const allDraws = useMemo(
    () => (mainDraw ? [mainDraw, ...linkedDraws] : linkedDraws),
    [linkedDraws, mainDraw],
  );

  const drawIdByProductId = useMemo(
    () => new Map(allDraws.map((draw) => [draw.draw.productId, draw.draw.id])),
    [allDraws],
  );
  const productIdByDrawId = useMemo(
    () => new Map(allDraws.map((draw) => [draw.draw.id, draw.draw.productId])),
    [allDraws],
  );

  const works = useMemo(() => {
    return allDraws
      .map((draw) => getProduct(draw.draw.productId))
      .filter((item): item is NonNullable<typeof item> => Boolean(item))
      .map(buildCheckoutWork);
  }, [allDraws, getProduct]);

  const enteredWorkIds = useMemo(() => {
    const enteredSet = new Set(enteredDrawIds);
    const workIds: string[] = [];

    for (const work of works) {
      const drawId = drawIdByProductId.get(work.id);
      if (drawId && enteredSet.has(drawId)) workIds.push(work.id);
    }

    return workIds;
  }, [drawIdByProductId, enteredDrawIds, works]);

  const selectedWorkIds = useMemo(() => {
    return selectedDraws
      .map((drawId) => productIdByDrawId.get(drawId))
      .filter((productId): productId is string => Boolean(productId));
  }, [productIdByDrawId, selectedDraws]);

  const allDrawsEntered = useMemo(() => {
    if (!allDraws.length) return false;

    const enteredSet = new Set(enteredDrawIds);
    return allDraws.every((item) => enteredSet.has(item.draw.id));
  }, [allDraws, enteredDrawIds]);

  const redirectedToEntriesRef = useRef(false);

  useEffect(() => {
    if (step.step === 'success') return;

    if (allDrawsEntered) {
      if (!redirectedToEntriesRef.current) {
        redirectedToEntriesRef.current = true;
        void router.replace(pathFor({ page: 'ACCOUNT_DRAW_ENTRIES' }));
      }
      return;
    }

    const enteredSet = new Set(enteredDrawIds);
    const remainingDraws = selectedDraws.filter(
      (drawId) => !enteredSet.has(drawId),
    );
    if (remainingDraws.length === selectedDraws.length) return;

    setSelectedDraws(remainingDraws);
    setMaxQuantity(Math.min(maxQuantity, Math.max(remainingDraws.length, 1)));

    if (step.step !== DEFAULT_CHECKOUT_STEP) {
      step.goToStep(DEFAULT_CHECKOUT_STEP);
    }
  }, [
    allDrawsEntered,
    enteredDrawIds,
    maxQuantity,
    router,
    selectedDraws,
    setMaxQuantity,
    setSelectedDraws,
    step,
  ]);

  const toggleWork = useCallback(
    (productId: string, quantity?: number): CheckoutSelectionChange | null => {
      if (quantity !== undefined) return null;
      if (isSingleDraw) return null;

      const drawId = drawIdByProductId.get(productId);
      if (!drawId) return null;
      if (isEntered(drawId)) return null;

      selectDraw(drawId);

      const nextSelectedDraws = selectedDraws.includes(drawId)
        ? selectedDraws.filter((selectedDrawId) => selectedDrawId !== drawId)
        : [...selectedDraws, drawId];
      const nextMaxQuantity = Math.min(
        maxQuantity,
        Math.max(nextSelectedDraws.length, 1),
      );

      setMaxQuantity(nextMaxQuantity);

      return { maxQuantity: nextMaxQuantity, selectedDraws: nextSelectedDraws };
    },
    [
      drawIdByProductId,
      isEntered,
      isSingleDraw,
      maxQuantity,
      selectDraw,
      selectedDraws,
      setMaxQuantity,
    ],
  );

  return useMemo(
    () => ({ enteredWorkIds, selectedWorkIds, toggleWork, works }),
    [enteredWorkIds, selectedWorkIds, toggleWork, works],
  );
};
